import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import { deleteItems } from "client/Api/Documents/WebDav/webDavActions";
import { HttpRequestError } from "client/Api/httpClient";
import {
	ConfirmationDialog,
	FormattedText,
	renderReactDialog
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog
} from "client/Hooks";
import {
	WebDavEvent,
	publish
} from "client_legacy/Documents/WebDav/events";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { NotAllItemsWereDeletedDialog } from "../../Dialogs/NotAllItemsWereDeletedDialog";

export type DeleteDocumentsDialogProps = {
	serverId: number;
	documentsToDelete: readonly FileSystemItemModel[];
	successCallback(): void;
	onClose(): void;
};

export const DeleteDocumentsDialog = (props: DeleteDocumentsDialogProps): ReactElement => {
	const {
		serverId,
		documentsToDelete,
		successCallback,
		onClose
	} = props;

	const [failedItems, setFailedItems] = useState<readonly FailedItem[]>();

	if (documentsToDelete.length <= 0) {
		throw new Error("Must have at least 1 document to be deleted");
	}

	const alertManager: AlertManager = useAlerts();
	const title: string | ReactElement = documentsToDelete.length > 1
		? (
			<FormattedText
				text={localize("_DeleteThisItems")}
				thingsToReplace={[
					{
						what: "{itemCount}",
						filler: documentsToDelete.length.toString()
					}
				]}
			/>
		) : localize("_DeleteThisItem");

	const deleteHandler = useCallback(
		async (): Promise<void> => {
			try {
				const documentIds: string[] = documentsToDelete.map(x => x.id);
				await deleteItems(serverId, documentIds);
				notifyAboutDeletedItems(serverId, documentsToDelete);
				successCallback();
				onClose();
			} catch (e) {
				let isHandled: boolean = false;
				if ((e instanceof HttpRequestError) && e.statusCode === 400) {
					try {
						const failedIds: readonly string[] = e.readJson<readonly string[]>();

						const [deletedDocuments, failedDocuments] = partition(
							documentsToDelete,
							(document) => {
								return failedIds.indexOf(document.id) === -1;
							}
						);

						if (deletedDocuments.length > 0) {
							notifyAboutDeletedItems(serverId, deletedDocuments);
						}

						isHandled = true;
						const failedItems: readonly FailedItem[] = failedDocuments.map(x => ({ name: x.name }));
						if (failedItems.length > 0) {
							setFailedItems(failedItems);
						} else {
							onClose();
						}
					} catch (e) {
						console.error(e);
					}
				}

				if (!isHandled) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}
 		},
		[
			alertManager,
			documentsToDelete,
			onClose,
			serverId,
			successCallback
		]
	);

	if (failedItems) {
		return (
			<NotAllItemsWereDeletedDialog
				failedItems={failedItems}
				onClose={onClose}
				showReasons={false}
			/>
		);
	}

	return (
		<ConfirmationDialog
			title={title}
			confirmText={localize("_Delete")}
			onCancellation={onClose}
			onConfirmation={deleteHandler}
			trackingId="delete-documents-dialog"
		>
			{localize("_ThisActionIsPermanentAndCannotBeUndone")}
		</ConfirmationDialog>
	);
};

DeleteDocumentsDialog.displayName = "DeleteDocumentsDialog";

type FailedItem = {
	name: string;
	reason?: string;
};

function notifyAboutDeletedItems(
	serverId: number,
	deletedItems: readonly FileSystemItemModel[]
): void {
	publish(
		WebDavEvent.Deleted,
		{
			serverId: serverId,
			documentIds: deletedItems.map(x => x.id)
		}
	);
}

export type DeleteDocumentsDialogInitialData = Omit<
	DeleteDocumentsDialogProps,
	"onClose"
>;

export function useDeleteDocumentsDialog(): [
	element: ReactElement | null,
	open: (props: DeleteDocumentsDialogInitialData) => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<DeleteDocumentsDialogInitialData, void>(
		(props, onClose) => (
			<DeleteDocumentsDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: DeleteDocumentsDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}

export function openDeleteDocumentsDialog(props: Omit<DeleteDocumentsDialogProps, "onClose">): void {
	renderReactDialog(
		(onClose) => (
			<DeleteDocumentsDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}